import React, { useEffect, useMemo, useRef, useState } from "react";

export function ChatPanel({ tenant, primary }) {
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content:
        "Hi — I'm here to help with web development, AI chatbots, or SaaS solutions. What are you looking to build or improve?"
    }
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadMode, setLeadMode] = useState(false);
  const [flowStep, setFlowStep] = useState(null);
  const [lead, setLead] = useState({
    name: "",
    phone: "",
    jobType: "",
    urgency: "idea",
    notes: ""
  });

  const scrollerRef = useRef(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, busy, leadMode]);

  const headerSubtitle = useMemo(() => {
    const area = (tenant.config.serviceArea || []).slice(0, 3).join(", ");
    return area ? `Service area: ${area}` : "Online";
  }, [tenant]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;

    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);

    try {
      const lower = text.toLowerCase();

      // WEBSITE FLOW
      if (flowStep === "website_type") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "What kind of business is the website for?"
          }
        ]);
        setFlowStep("website_business");
        setBusy(false);
        return;
      }

      if (flowStep === "website_business") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Do you need lead generation features like contact forms, chatbots, or booking?"
          }
        ]);
        setFlowStep("website_features");
        setBusy(false);
        return;
      }

      if (flowStep === "website_features") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Great — this sounds like a good fit. Please share your name, phone, and a few notes about the website you want, and I’ll get your project details started."
          }
        ]);
        setLead((s) => ({ ...s, jobType: "Website" }));
        setLeadMode(true);
        setFlowStep(null);
        setBusy(false);
        return;
      }

      // CHATBOT FLOW
      if (flowStep === "chatbot_type") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "What do you want the chatbot to handle — lead capture, customer support, automation, or all of the above?"
          }
        ]);
        setFlowStep("chatbot_goal");
        setBusy(false);
        return;
      }

      if (flowStep === "chatbot_goal") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Will this be used on your website, inside your business, or both?"
          }
        ]);
        setFlowStep("chatbot_location");
        setBusy(false);
        return;
      }

      if (flowStep === "chatbot_location") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Great — this sounds like a strong fit. Please share your name, phone, and a few details about what you want the chatbot to do."
          }
        ]);
        setLead((s) => ({ ...s, jobType: "Chatbot" }));
        setLeadMode(true);
        setFlowStep(null);
        setBusy(false);
        return;
      }

      // SAAS FLOW
      if (flowStep === "saas_stage") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "What kind of SaaS are you building — internal business tool, client platform, marketplace, or something else?"
          }
        ]);
        setFlowStep("saas_type");
        setBusy(false);
        return;
      }

      if (flowStep === "saas_type") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Do you need an MVP, a redesign, or new features added to an existing product?"
          }
        ]);
        setFlowStep("saas_scope");
        setBusy(false);
        return;
      }

      if (flowStep === "saas_scope") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Understood. Please share your name, phone, and a few notes about the SaaS product you want to build."
          }
        ]);
        setLead((s) => ({ ...s, jobType: "SaaS" }));
        setLeadMode(true);
        setFlowStep(null);
        setBusy(false);
        return;
      }

      // LEAD GENERATION FLOW
      if (flowStep === "lead_goal") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Are you trying to generate more calls, form submissions, booked appointments, or all of the above?"
          }
        ]);
        setFlowStep("lead_type");
        setBusy(false);
        return;
      }

      if (flowStep === "lead_type") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Is this for an existing website, or do you also need a new site or landing page?"
          }
        ]);
        setFlowStep("lead_site_status");
        setBusy(false);
        return;
      }

      if (flowStep === "lead_site_status") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Got it. Please share your name, phone, and a few notes about your business and lead generation goals."
          }
        ]);
        setLead((s) => ({ ...s, jobType: "Lead Generation" }));
        setLeadMode(true);
        setFlowStep(null);
        setBusy(false);
        return;
      }

      // GENERAL CONTACT FLOW
      if (flowStep === "contact_type") {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Please share your name, phone, and a quick note about what you’d like to discuss."
          }
        ]);
        setLead((s) => ({ ...s, jobType: "General Inquiry" }));
        setLeadMode(true);
        setFlowStep(null);
        setBusy(false);
        return;
      }

      // START FLOWS
      if (lower.includes("website") || lower.includes("site") || lower.includes("web design") || lower.includes("web development")) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Are you looking for a brand new site or updates to a current one?"
          }
        ]);
        setFlowStep("website_type");
        setBusy(false);
        return;
      }

      if (lower.includes("chatbot") || lower.includes("bot")) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Are you looking to add a chatbot to an existing system or build something new?"
          }
        ]);
        setFlowStep("chatbot_type");
        setBusy(false);
        return;
      }

      if (lower.includes("saas")) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Are you starting a new SaaS product or improving an existing one?"
          }
        ]);
        setFlowStep("saas_stage");
        setBusy(false);
        return;
      }

      if (lower.includes("lead") || lower.includes("leads") || lower.includes("more calls") || lower.includes("appointments") || lower.includes("conversions")) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Are you mainly trying to improve lead generation, conversion rate, or both?"
          }
        ]);
        setFlowStep("lead_goal");
        setBusy(false);
        return;
      }

      if (
        lower.includes("contact") ||
        lower.includes("consult") ||
        lower.includes("consultation") ||
        lower.includes("talk") ||
        lower.includes("speak") ||
        lower.includes("help")
      ) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "Sure — is this about a website, chatbot, SaaS product, or general project discussion?"
          }
        ]);
        setFlowStep("contact_type");
        setBusy(false);
        return;
      }

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "We help with websites, chatbots, SaaS products, and lead generation systems. Are you looking for a new build, an improvement to something existing, or just exploring options?"
        }
      ]);
      setBusy(false);
      return;
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "There was a problem processing that. Please try again." }
      ]);
    } finally {
      setBusy(false);
    }
  }

  async function submitLead() {
    setBusy(true);

    try {
      const submission = new FormData();

      submission.append("name", lead.name);
      submission.append("phone", lead.phone);
      submission.append("projectType", lead.jobType);
      submission.append("projectStage", lead.urgency);
      submission.append("notes", lead.notes);

      submission.append("_subject", "New Yokai Software Chatbot Lead");
      submission.append("_template", "table");
      submission.append("_captcha", "false");
      submission.append("_next", window.location.href);

      const response = await fetch("https://formsubmit.co/ajax/contact@yokaisoftware.com", {
        method: "POST",
        headers: {
          Accept: "application/json"
        },
        body: submission
      });

      const result = await response.json();

      if (result.success === "true" || response.ok) {
        setLeadMode(false);
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content:
              "Got it — your project details are in. We’ll review this and reach out to discuss scope, timeline, and pricing."
          }
        ]);

        setLead({
          name: "",
          phone: "",
          jobType: "",
          urgency: "idea",
          notes: ""
        });
      } else {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "There was a problem sending your details. Please try again."
          }
        ]);
      }
    } catch (error) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "There was a problem sending your details. Please try again."
        }
      ]);
    } finally {
      setBusy(false);
    }
  }

  const calendly = tenant.config.scheduling?.enabled ? tenant.config.scheduling?.calendlyUrl : "";
  const checkout = tenant.config.payments?.enabled ? tenant.config.payments?.checkoutUrl : "";
  const phone = tenant.config.voice?.enabled ? tenant.config.voice?.phone : "";

  return (
    <div style={styles.shell}>
      <div style={{ ...styles.header, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <div style={styles.headerRow}>
          <div style={styles.brandDot(primary)} />
          <div>
            <div style={styles.title}>{tenant.name}</div>
            <div style={styles.subtitle}>{headerSubtitle}</div>
          </div>
        </div>

        <div style={styles.actions}>
          {calendly ? (
            <a style={styles.linkBtn(primary)} href={calendly} target="_blank" rel="noreferrer">
              Schedule
            </a>
          ) : null}
          {checkout ? (
            <a style={styles.linkBtn(primary)} href={checkout} target="_blank" rel="noreferrer">
              Pay
            </a>
          ) : null}
          {phone ? (
            <a style={styles.linkBtn(primary)} href={`tel:${phone}`}>
              Call
            </a>
          ) : null}
        </div>
      </div>

      <div ref={scrollerRef} style={styles.scroller}>
        {messages.map((m, idx) => (
          <Bubble key={idx} role={m.role} text={m.content} primary={primary} />
        ))}
        {busy ? <div style={styles.thinking}>…</div> : null}
      </div>

      {leadMode ? (
        <div style={styles.leadBox}>
          <div style={styles.leadTitle}>Project details</div>
          <div style={styles.grid}>
            <Field label="Name" value={lead.name} onChange={(v) => setLead((s) => ({ ...s, name: v }))} />
            <Field label="Phone" value={lead.phone} onChange={(v) => setLead((s) => ({ ...s, phone: v }))} />
            <Field label="Project type" value={lead.jobType} onChange={(v) => setLead((s) => ({ ...s, jobType: v }))} />

            <select
              value={lead.urgency}
              onChange={(e) => setLead((s) => ({ ...s, urgency: e.target.value }))}
              style={styles.select}
            >
              <option value="idea">Just planning / idea stage</option>
              <option value="soon">Looking to start soon</option>
              <option value="asap">Ready to start ASAP</option>
            </select>

            <textarea
              placeholder="Notes (optional)"
              value={lead.notes}
              onChange={(e) => setLead((s) => ({ ...s, notes: e.target.value }))}
              style={styles.textarea}
            />
          </div>

          <div style={styles.leadFooter}>
            <button onClick={() => setLeadMode(false)} style={styles.ghostBtn}>
              Cancel
            </button>
            <button
              onClick={submitLead}
              style={styles.primaryBtn(primary)}
              disabled={busy || !lead.phone || !lead.jobType}
            >
              Submit
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.inputRow}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message…"
            style={styles.input}
            onKeyDown={(e) => (e.key === "Enter" ? send() : null)}
            disabled={busy}
          />
          <button onClick={send} style={styles.sendBtn(primary)} disabled={busy || !input.trim()}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

function Bubble({ role, text, primary }) {
  const isUser = role === "user";
  return (
    <div style={{ ...styles.bubbleRow, justifyContent: isUser ? "flex-end" : "flex-start" }}>
      <div
        style={{
          ...styles.bubble,
          background: isUser ? primary : "rgba(0,0,0,0.06)",
          color: isUser ? "#fff" : "#111",
          borderTopLeftRadius: isUser ? 16 : 6,
          borderTopRightRadius: isUser ? 6 : 16
        }}
      >
        {text}
      </div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <label style={styles.field}>
      <div style={styles.label}>{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={styles.fieldInput} />
    </label>
  );
}

const styles = {
  shell: { width: "90%", height: "95%", display: "flex", flexDirection: "column" },
  header: { padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  headerRow: { display: "flex", alignItems: "center", gap: 10 },
  brandDot: (primary) => ({ width: 10, height: 10, borderRadius: 999, background: primary }),
  title: { fontSize: 14, fontWeight: 800, lineHeight: 1.1 },
  subtitle: { fontSize: 12, opacity: 0.7, marginTop: 2 },
  actions: { display: "flex", gap: 8 },
  linkBtn: {
    fontSize: 12,
    padding: "8px 10px",
    borderRadius: 999,
    textDecoration: "none",
    border: "1px solid rgba(0,0,0,0.12)",
    color: "#111",
    background: "#fff"
  },
  scroller: { flex: 1, overflowY: "auto", padding: 12, background: "#fff" },
  thinking: { fontSize: 12, opacity: 0.6, padding: 6 },
  bubbleRow: { display: "flex", marginBottom: 10 },
  bubble: { maxWidth: "85%", padding: "10px 12px", borderRadius: 16, fontSize: 14, whiteSpace: "pre-wrap" },
  inputRow: {
    display: "flex",
    gap: 8,
    padding: 12,
    borderTop: "1px solid rgba(0,0,0,0.08)",
    background: "#fff"
  },
  input: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.14)",
    outline: "none",
    fontSize: 16
  },
  sendBtn: (primary) => ({
    padding: "10px 14px",
    borderRadius: 12,
    border: "none",
    background: primary,
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer"
  }),
  leadBox: {
    borderTop: "1px solid rgba(0,0,0,0.08)",
    padding: 12,
    background: "#fff"
  },
  leadTitle: { fontSize: 13, fontWeight: 800, marginBottom: 10 },
  grid: { display: "grid", gridTemplateColumns: "1fr", gap: 10 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, opacity: 0.7 },
  fieldInput: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.14)",
    fontSize: 16
  },
  select: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.14)",
    fontSize: 16
  },
  textarea: {
    gridColumn: "1 / -1",
    minHeight: 72,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.14)",
    fontSize: 16,
    resize: "vertical"
  },
  leadFooter: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 },
  ghostBtn: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.14)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700
  },
  primaryBtn: (primary) => ({
    padding: "10px 14px",
    borderRadius: 12,
    border: "none",
    background: primary,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 800
  })
};








/*import React, { useEffect, useMemo, useRef, useState } from "react";


export function ChatPanel({ tenant, primary }) {
  const tenantId = tenant.tenantId;
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content: `Hi — I'm here to help with web development, AI chatbots, or SaaS solutions. What are you looking to build or improve?`
    }
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadMode, setLeadMode] = useState(false);
  const [flowStep, setFlowStep] = useState(null);
  const [lead, setLead] = useState({
    name: "",
    phone: "",
    address: "",
    jobType: "",
    urgency: "normal",
    notes: ""
  });

  const scrollerRef = useRef(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, busy, leadMode]);

  const headerSubtitle = useMemo(() => {
    const area = (tenant.config.serviceArea || []).slice(0, 3).join(", ");
    return area ? `Service area: ${area}` : "Online";
  }, [tenant]);

 async function send() {
  const text = input.trim();
  if (!text || busy) return;

  setMessages((m) => [...m, { role: "user", content: text }]);
  setInput("");
  setBusy(true);

  try {
    const lower = text.toLowerCase();

    // WEBSITE FLOW
    if (flowStep === "website_type") {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "What kind of business is the website for?"
        }
      ]);
      setFlowStep("website_business");
      setBusy(false);
      return;
    }

    if (flowStep === "website_business") {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "Do you need lead generation features like contact forms, chatbots, or booking?"
        }
      ]);
      setFlowStep("website_features");
      setBusy(false);
      return;
    }

    if (flowStep === "website_features") {
  setMessages((m) => [
    ...m,
    {
      role: "assistant",
      content: "Great — this sounds like a good fit. Please share your name, phone, and a few notes about the website you want, and I’ll get your project details started."
    }
  ]);
  setLeadMode(true);
  setFlowStep(null);
  setBusy(false);
  return;
}

    // START WEBSITE FLOW
    if (lower.includes("website")) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "Are you looking for a brand new site or updates to a current one?"
        }
      ]);
      setFlowStep("website_type");
      setBusy(false);
      return;
    }

    // STATIC-ONLY RESPONSES
if (lower.includes("chatbot")) {
  setMessages((m) => [
    ...m,
    {
      role: "assistant",
      content: "Yes — we build chatbot systems for websites, lead capture, and automation. Is this for your website, your internal workflow, or both?"
    }
  ]);
  setBusy(false);
  return;
}

if (lower.includes("saas")) {
  setMessages((m) => [
    ...m,
    {
      role: "assistant",
      content: "Yes — we help plan and build SaaS products. Are you starting a new product or improving an existing one?"
    }
  ]);
  setBusy(false);
  return;
}

if (lower.includes("lead")) {
  setMessages((m) => [
    ...m,
    {
      role: "assistant",
      content: "We help businesses improve lead flow through better websites, chatbots, and conversion-focused features. Are you looking for more calls, form leads, or booked appointments?"
    }
  ]);
  setBusy(false);
  return;
}

setMessages((m) => [
  ...m,
  {
    role: "assistant",
    content: "We help with websites, chatbots, and SaaS projects. What are you looking to build or improve?"
  }
]);
setBusy(false);
return;
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Lead submit error: " + String(e?.message || e) }
      ]);
    } finally {
      setBusy(false);
    }
  }

  const calendly = tenant.config.scheduling?.enabled ? tenant.config.scheduling?.calendlyUrl : "";
  const checkout = tenant.config.payments?.enabled ? tenant.config.payments?.checkoutUrl : "";
  const phone = tenant.config.voice?.enabled ? tenant.config.voice?.phone : "";


  async function submitLead() {
  setBusy(true);

  try {
    const submission = new FormData();

    submission.append("name", lead.name);
    submission.append("phone", lead.phone);
    submission.append("projectType", lead.jobType);
    submission.append("projectStage", lead.urgency);
    submission.append("notes", lead.notes);

    submission.append("_subject", "New Yokai Software Chatbot Lead");
    submission.append("_template", "table");
    submission.append("_captcha", "false");
    submission.append("_next", window.location.href);

    const response = await fetch("https://formsubmit.co/ajax/contact@yokaisoftware.com", {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: submission,
    });

    const result = await response.json();

    if (result.success === "true" || response.ok) {
      setLeadMode(false);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Got it — your project details are in. We’ll review this and reach out to discuss scope, timeline, and pricing."
        }
      ]);

      setLead({
        name: "",
        phone: "",
        address: "",
        jobType: "",
        urgency: "normal",
        notes: ""
      });
    } else {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "There was a problem sending your details. Please try again."
        }
      ]);
    }
  } catch (error) {
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        content: "There was a problem sending your details. Please try again."
      }
    ]);
  } finally {
    setBusy(false);
  }
}

  return (
    <div style={styles.shell}>
      <div style={{ ...styles.header, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <div style={styles.headerRow}>
          <div style={styles.brandDot(primary)} />
          <div>
            <div style={styles.title}>{tenant.name}</div>
            <div style={styles.subtitle}>{headerSubtitle}</div>
          </div>
        </div>

        <div style={styles.actions}>
          {calendly ? (
            <a style={styles.linkBtn(primary)} href={calendly} target="_blank" rel="noreferrer">
              Schedule
            </a>
          ) : null}
          {checkout ? (
            <a style={styles.linkBtn(primary)} href={checkout} target="_blank" rel="noreferrer">
              Pay
            </a>
          ) : null}
          {phone ? (
            <a style={styles.linkBtn(primary)} href={`tel:${phone}`}>
              Call
            </a>
          ) : null}
        </div>
      </div>

      <div ref={scrollerRef} style={styles.scroller}>
        {messages.map((m, idx) => (
          <Bubble key={idx} role={m.role} text={m.content} primary={primary} />
        ))}
        {busy ? <div style={styles.thinking}>…</div> : null}
      </div>

      {leadMode ? (
        <div style={styles.leadBox}>
          <div style={styles.leadTitle}>Estimate details</div>
          <div style={styles.grid}>
            <Field label="Name" value={lead.name} onChange={(v) => setLead((s) => ({ ...s, name: v }))} />
            <Field label="Phone" value={lead.phone} onChange={(v) => setLead((s) => ({ ...s, phone: v }))} />
          
            <Field label="Project type" value={lead.jobType} onChange={(v) => setLead((s) => ({ ...s, jobType: v }))} />
            <select
              value={lead.urgency}
              onChange={(e) => setLead((s) => ({ ...s, urgency: e.target.value }))}
              style={styles.select}
            >
              <option value="idea">Just planning / idea stage</option>
              <option value="soon">Looking to start soon</option>
              <option value="asap">Ready to start ASAP</option>
            </select>
            <textarea
              placeholder="Notes (optional)"
              value={lead.notes}
              onChange={(e) => setLead((s) => ({ ...s, notes: e.target.value }))}
              style={styles.textarea}
            />
          </div>
          <div style={styles.leadFooter}>
            <button onClick={() => setLeadMode(false)} style={styles.ghostBtn}>
              Cancel
            </button>
            <button onClick={submitLead} style={styles.primaryBtn(primary)} disabled={busy || !lead.phone || !lead.jobType}>
              Submit
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.inputRow}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message…"
            style={styles.input}
            onKeyDown={(e) => (e.key === "Enter" ? send() : null)}
            disabled={busy}
          />
          <button onClick={send} style={styles.sendBtn(primary)} disabled={busy || !input.trim()}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

function Bubble({ role, text, primary }) {
  const isUser = role === "user";
  return (
    <div style={{ ...styles.bubbleRow, justifyContent: isUser ? "flex-end" : "flex-start" }}>
      <div
        style={{
          ...styles.bubble,
          background: isUser ? primary : "rgba(0,0,0,0.06)",
          color: isUser ? "#fff" : "#111",
          borderTopLeftRadius: isUser ? 16 : 6,
          borderTopRightRadius: isUser ? 6 : 16
        }}
      >
        {text}
      </div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <label style={styles.field}>
      <div style={styles.label}>{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={styles.fieldInput} />
    </label>
  );
}

const styles = {
  shell: { width: "90%", height: "95%", display: "flex", flexDirection: "column", },
  header: { padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  headerRow: { display: "flex", alignItems: "center", gap: 10 },
  brandDot: (primary) => ({ width: 10, height: 10, borderRadius: 999, background: primary }),
  title: { fontSize: 14, fontWeight: 800, lineHeight: 1.1 },
  subtitle: { fontSize: 12, opacity: 0.7, marginTop: 2 },
  actions: { display: "flex", gap: 8 },
  linkBtn: (primary) => ({
    fontSize: 12,
    padding: "8px 10px",
    borderRadius: 999,
    textDecoration: "none",
    border: "1px solid rgba(0,0,0,0.12)",
    color: "#111",
    background: "#fff"
  }),
  scroller: { flex: 1, overflowY: "auto", padding: 12, background: "#fff" },
  thinking: { fontSize: 12, opacity: 0.6, padding: 6 },
  bubbleRow: { display: "flex", marginBottom: 10 },
  bubble: { maxWidth: "85%", padding: "10px 12px", borderRadius: 16, fontSize: 14, whiteSpace: "pre-wrap" },
  inputRow: {
    display: "flex",
    gap: 8,
    padding: 12,
    borderTop: "1px solid rgba(0,0,0,0.08)",
    background: "#fff"
  },
  input: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.14)",
    outline: "none",
    fontSize: 14
  },
  sendBtn: (primary) => ({
    padding: "10px 14px",
    borderRadius: 12,
    border: "none",
    background: primary,
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer"
  }),
  leadBox: {
    borderTop: "1px solid rgba(0,0,0,0.08)",
    padding: 12,
    background: "#fff"
  },
  leadTitle: { fontSize: 13, fontWeight: 800, marginBottom: 10 },
  //grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  grid: { display: "grid", gridTemplateColumns: "1fr", gap: 10 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, opacity: 0.7 },
  fieldInput: { padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(0,0,0,0.14)", fontSize: 14 },
  select: { padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(0,0,0,0.14)", fontSize: 14 },
  textarea: {
    gridColumn: "1 / -1",
    minHeight: 72,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.14)",
    fontSize: 14,
    resize: "vertical"
  },
  leadFooter: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 },
  ghostBtn: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.14)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700
  },
  primaryBtn: (primary) => ({
    padding: "10px 14px",
    borderRadius: 12,
    border: "none",
    background: primary,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 800
  })
};*/
